import React, { FC } from 'react';
import cn from 'classnames';
import styled, { css, keyframes } from 'styled-components';

/** Mirrors `UrlIcon`'s own size fallback, so the placeholder fills the icon's space. */
export const DEFAULT_ICON_SIZE = '100px';

export interface IIconPlaceholderProps {
  width?: string;
  height?: string;
  /** Must receive the icon's `className` — call sites size icons through it. */
  className?: string;
  loading?: boolean;
}

/**
 * Purely decorative (`aria-hidden`) and deliberately text-free: its only job is to hold
 * an icon's layout slot while it loads, or after it has permanently failed.
 */
export const IconPlaceholder: FC<IIconPlaceholderProps> = ({
  width,
  height,
  className,
  loading = false
}) => (
  <SPlaceholder
    data-testid="icon-placeholder"
    className={cn(className)}
    $width={width}
    $height={height}
    $loading={loading}
    aria-hidden="true"
  />
);

const pulse = keyframes`
  0% { opacity: 0.5; }
  50% { opacity: 1; }
  100% { opacity: 0.5; }
`;

const SPlaceholder = styled.div<{
  $width?: string;
  $height?: string;
  $loading?: boolean;
}>`
  min-width: ${({ $width }) => $width || DEFAULT_ICON_SIZE};
  min-height: ${({ $height }) => $height || DEFAULT_ICON_SIZE};
  width: ${({ $width }) => $width || DEFAULT_ICON_SIZE};
  height: ${({ $height }) => $height || DEFAULT_ICON_SIZE};
  /* Centred like the real icons: SWrapper is text-align:center, which a fixed
     width block box would otherwise ignore. */
  margin: 0 auto;
  border-radius: 12px;
  background-color: var(--color-grey);
  opacity: 0.16;
  ${({ $loading }) =>
    $loading &&
    css`
      animation: ${pulse} 1.4s ease-in-out infinite;
    `}
`;
